import { Component, forwardRef, input, OnDestroy, output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

type SelectValue = string | number | null;

export interface SelectOption<T extends SelectValue> {
  label: string;
  value: T;
}

@Component({
  selector: 'app-custom-select',
  templateUrl: './custom-select.component.html',
  styleUrls: ['./custom-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomSelectComponent),
      multi: true
    }],
  standalone: false
})
export class CustomSelectComponent implements OnDestroy, ControlValueAccessor {

  label = input<string>();
  placeholder = input<string>('Select option');
  isValidInput = input<boolean>(false);
  isInvalidInput = input<boolean>(false);
  options = input<{ label: string; value: SelectValue }[]>([]);
  errorMessage = input<string | null>(null);
  showSuffixIcon = input<boolean>(false);
  suffixIconPath = input<string | null>(null);
  iconSize = input<number>(20);
  ionBlur = output<void>();
  iconClick = output<void>();
  customClass = input<string>('');
  listCustomClass = input<string>('');
  labelPrefix = input<string>('');
  value: SelectValue = null;
  disabled: boolean = false;
  isOpen = false;
  popoverEvent: any;

  /* ================= for ControlValueAccessor ================= */

  private onChange: (value: SelectValue) => void = () => { };
  private onTouched: () => void = () => { };

  writeValue(value: SelectValue): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /* ============== UI Logic =============== */

  selectedLabel = () => {
    if (this.value)
      return this.labelPrefix() + this.options().find(o => o.value === this.value)?.label;
    return null;
  }

  open(ev: Event) {
    if (this.disabled) return;
    this.popoverEvent = ev;
    this.isOpen = true;
  }

  close() {
    this.isOpen = false;
    this.onTouched();
    this.ionBlur.emit();
  }

  select(ev: CustomEvent) {
    const val = ev.detail.value as SelectValue;
    this.value = val;
    this.onChange(val);
    this.close();
  }

  onIconClick(event: Event) {
    event.stopPropagation();
    this.iconClick.emit();
  }

  ngOnDestroy() {
    this.isOpen = false;
    this.popoverEvent = null;
  }
}
